import QRCodeStyling, { type CornerSquareType, type DotType } from 'qr-code-styling';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@qr/ui';

/**
 * Nuestro vocabulario de formas es el que ve el usuario en el panel; la
 * libreria usa el suyo. El mapeo vive aqui para que un cambio de libreria no
 * obligue a migrar los disenos ya guardados en la base de datos.
 */
const MODULE_SHAPES: Record<'square' | 'dots' | 'rounded', DotType> = {
  square: 'square',
  dots: 'dots',
  rounded: 'rounded',
};

const EYE_SHAPES: Record<'square' | 'circle' | 'rounded', CornerSquareType> = {
  square: 'square',
  circle: 'dot',
  rounded: 'extra-rounded',
};

/**
 * Vista previa y descarga del codigo QR.
 *
 * Lo que se codifica es SIEMPRE el enlace corto, nunca el destino final: esa
 * es la razon de ser del producto. Cambiar el destino no cambia ni un pixel
 * del codigo, asi que lo impreso sigue valiendo.
 *
 * La correccion de errores va en nivel H (recupera hasta un 30% del codigo
 * danado) porque el logo tapa el centro. Con un nivel menor, un codigo con
 * logo deja de leerse en cuanto la impresion no es perfecta.
 *
 * Sobre el temporizador de mas abajo: la libreria descarga el logo con una
 * peticion que no lleva manejador de error. Si esa peticion no llega a
 * completarse —la bloquea la CSP, se cae la red, cambia el dominio del
 * almacenamiento— su promesa no se resuelve nunca, el SVG se queda vacio y no
 * se registra nada en consola. Paso de verdad, y el sintoma fue un panel con
 * un hueco en blanco y ni una pista de por que. El temporizador convierte ese
 * silencio en un codigo sin logo y un aviso, que es un fallo del que se puede
 * salir.
 */

/** Margen antes de dar el logo por perdido. De sobra incluso con mala red. */
const ESPERA_LOGO_MS = 6000;

/**
 * Lado del PNG que se descarga.
 *
 * NO es el de la vista previa, y eso importa: antes se descargaba al tamano
 * que se estuviera mostrando, unos 200 pixeles, que en pantalla se ve bien y
 * en un cartel impreso es un borron. 1024 aguanta una etiqueta grande a 300
 * puntos por pulgada, y como el QR son cuadrados planos el archivo sigue
 * pesando poco.
 *
 * El SVG no lo necesita —es vectorial y escala solo—, pero se genera con el
 * mismo tamano para que el logo incrustado tampoco salga pixelado.
 */
const DESCARGA_PX = 1024;

/** Lo que define como se dibuja un codigo, sin nada de React. */
export interface DisenoQr {
  readonly value: string;
  readonly foreground?: string | undefined;
  readonly background?: string | undefined;
  readonly logoUrl?: string | null | undefined;
  readonly moduleShape?: 'square' | 'dots' | 'rounded' | undefined;
  readonly eyeShape?: 'square' | 'circle' | 'rounded' | undefined;
}

/**
 * Opciones para la libreria, a un lado del arbol de React.
 *
 * Vive fuera del componente porque descargar un codigo no deberia exigir
 * tenerlo pintado: desde la lista se descarga sin abrir el detalle.
 */
function opcionesQr(lado: number, d: DisenoQr) {
  const {
    value,
    foreground = '#101418',
    background = '#ffffff',
    logoUrl = null,
    moduleShape = 'square',
    eyeShape = 'square',
  } = d;

  return {
    width: lado,
    height: lado,
    type: 'svg' as const,
    data: value,
    margin: Math.round(lado * 0.04),
    qrOptions: { errorCorrectionLevel: 'H' as const },
    dotsOptions: { color: foreground, type: MODULE_SHAPES[moduleShape] },
    cornersSquareOptions: { color: foreground, type: EYE_SHAPES[eyeShape] },
    backgroundOptions: { color: background },
    // El spread condicional y no `image: logoUrl ?? undefined`: con
    // exactOptionalPropertyTypes, pasar `undefined` explicitamente no es lo
    // mismo que omitir la propiedad.
    ...(logoUrl ? { image: logoUrl } : {}),
    imageOptions: {
      crossOrigin: 'anonymous',
      // Un respiro entre el logo y los modulos, proporcional al lienzo para
      // que la vista previa y la descarga se vean igual: en esta libreria el
      // margen esta en pixeles del lienzo, asi que uno fijo se ve holgado a
      // 200 px e inexistente a 1024.
      //
      // Ojo al ajustarlo: el margen se RESTA al logo, no se suma alrededor.
      // Subirlo a secas lo encoge, por eso `imageSize` va emparejado.
      margin: Math.round(lado * 0.015),
      // El hueco que se despeja es cuadrado, asi que un logo apaisado deja
      // bandas arriba y abajo. Eso se avisa al subirlo; aqui no tiene arreglo.
      imageSize: 0.42,
      hideBackgroundDots: true,
    },
  };
}

/**
 * Descarga un codigo a tamano de imprenta desde donde haga falta.
 *
 * Siempre a `DESCARGA_PX`, nunca al tamano que se estuviera mostrando: lo que
 * se ve en pantalla son 200 pixeles y eso impreso en un cartel es un borron.
 */
export async function descargarQr(
  diseno: DisenoQr,
  fileName: string,
  extension: 'svg' | 'png',
): Promise<void> {
  const qr = new QRCodeStyling(opcionesQr(DESCARGA_PX, diseno));
  await qr.download({ name: fileName, extension });
}

interface QrPreviewProps {
  readonly value: string;
  readonly foreground?: string;
  readonly background?: string;
  readonly logoUrl?: string | null | undefined;
  readonly moduleShape?: 'square' | 'dots' | 'rounded';
  readonly eyeShape?: 'square' | 'circle' | 'rounded';
  readonly size?: number;
  readonly fileName?: string;
  readonly showDownloads?: boolean;
}

export function QrPreview({
  value,
  foreground = '#101418',
  background = '#ffffff',
  logoUrl = null,
  moduleShape = 'square',
  eyeShape = 'square',
  size = 220,
  fileName = 'codigo-qr',
  showDownloads = true,
}: QrPreviewProps) {
  const container = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [logoPerdido, setLogoPerdido] = useState(false);

  // Si cambia el logo se le da otra oportunidad: puede que el anterior
  // fallara y este cargue sin problema.
  const [logoVisto, setLogoVisto] = useState(logoUrl);
  if (logoUrl !== logoVisto) {
    setLogoVisto(logoUrl);
    setLogoPerdido(false);
  }

  const logoEfectivo = logoPerdido ? null : logoUrl;

  const diseno = useMemo(
    (): DisenoQr => ({
      value,
      foreground,
      background,
      moduleShape,
      eyeShape,
      logoUrl: logoEfectivo,
    }),
    [value, foreground, background, moduleShape, eyeShape, logoEfectivo],
  );

  const qr = useMemo(
    () => new QRCodeStyling(opcionesQr(size, diseno)),
    // Se reconstruye entero cuando cambia el diseno: la libreria no expone
    // una actualizacion parcial fiable para todas estas opciones.
    [diseno, size],
  );

  useEffect(() => {
    const node = container.current;
    if (!node) return;
    node.replaceChildren();
    qr.append(node);

    if (!logoEfectivo) return;

    const aviso = setTimeout(() => {
      // El SVG se crea vacio y se rellena al terminar de dibujar. Si sigue
      // teniendo solo el <defs> pasado este tiempo, el dibujado se quedo
      // colgado esperando el logo.
      const svg = node.firstElementChild;
      if (svg && svg.childElementCount <= 1) setLogoPerdido(true);
    }, ESPERA_LOGO_MS);

    return () => clearTimeout(aviso);
  }, [qr, logoEfectivo]);

  async function download(extension: 'svg' | 'png') {
    setBusy(true);
    try {
      // SVG para imprenta (escala sin perder nitidez), PNG para pantalla y
      // para quien solo sabe pegar imagenes.
      await descargarQr(diseno, fileName, extension);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="qr-preview">
      <div
        className="qr-preview__canvas"
        ref={container}
        role="img"
        aria-label={`Codigo QR que apunta a ${value}`}
      />
      {logoPerdido ? (
        <p className="qr-preview__aviso">
          No se ha podido cargar el logo, así que el código se muestra sin el.
          Comprueba tu conexion y vuelve a intentarlo.
        </p>
      ) : null}
      {showDownloads ? (
        <div className="qr-preview__actions">
          <Button size="sm" variant="secondary" onClick={() => download('svg')} loading={busy}>
            Descargar SVG
          </Button>
          <Button size="sm" variant="secondary" onClick={() => download('png')} loading={busy}>
            Descargar PNG
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * El SVG de un codigo, como texto.
 *
 * Para la creacion masiva: cincuenta codigos en un ZIP. Se genera a tamano de
 * imprenta igual que una descarga suelta, y en SVG porque pesa una fraccion de
 * lo que pesaria el PNG y escala sin perder nitidez.
 */
export async function svgDeQr(diseno: DisenoQr): Promise<string> {
  const qr = new QRCodeStyling(opcionesQr(DESCARGA_PX, diseno));
  const datos = await qr.getRawData('svg');
  if (!datos) throw new Error('No se pudo generar el codigo');
  return datos instanceof Blob ? datos.text() : new TextDecoder().decode(datos);
}
